// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Utility functions for building a directed hierarchy from Digital Twin
 * entities and relationships.
 *
 * The hierarchy represents the equipment tree (excluding Site entities)
 * with parent→child edges derived from `feeds`, `containsEquipment`,
 * and `powers` relationships.
 */

import type { Entity, Relationship } from '../types/digitalTwin';
import { cleanEntityType, getEntityDisplayType, getTelemetryEntityIds } from './entityHelpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HierarchyNode {
  entity: Entity;
  displayType: string;
  children: HierarchyNode[];
  parent: HierarchyNode | null;
}

// ---------------------------------------------------------------------------
// Hierarchy builder
// ---------------------------------------------------------------------------

/** Relationship types that establish parent→child (source→target) direction. */
const HIERARCHY_RELATIONSHIP_TYPES = new Set(['feeds', 'containsEquipment', 'powers']);

/**
 * Build a directed tree from entities and relationships.
 *
 * - Filters out Site entities (they are the container, not equipment).
 * - Filters out telemetry points (targets of hasTelemetryPoint/hasPoint
 *   edges) so they never surface as root-level "assets" — this is a
 *   structural check on the graph itself, independent of whatever
 *   type-name allow-list a caller may have already applied.
 * - Uses `feeds`, `containsEquipment`, and `powers` relationships to
 *   establish parent→child edges (source is parent, target is child).
 * - Entities with no incoming hierarchy edges become root nodes.
 * - Falls back to a flat list if no relationships exist.
 */
export function buildHierarchy(entities: Entity[], relationships: Relationship[]): HierarchyNode[] {
  // Filter out Site entities and telemetry points
  const telemetryIds = getTelemetryEntityIds(relationships);
  const equipment = entities.filter(
    e => cleanEntityType(e.type) !== 'Site' && !telemetryIds.has(e.id)
  );

  // Build a node map
  const nodeMap = new Map<string, HierarchyNode>();
  for (const entity of equipment) {
    nodeMap.set(entity.id, {
      entity,
      displayType: getEntityDisplayType(entity.type),
      children: [],
      parent: null,
    });
  }

  // Wire up parent→child edges from hierarchy relationships
  const hierarchyRels = relationships.filter(r => HIERARCHY_RELATIONSHIP_TYPES.has(r.type));

  for (const rel of hierarchyRels) {
    const parentNode = nodeMap.get(rel.source);
    const childNode = nodeMap.get(rel.target);
    if (parentNode && childNode && childNode.parent === null) {
      // Cycle guard: walk the parent's ancestor chain to ensure the
      // child isn't already an ancestor (prevents A→B→C→A rings).
      let ancestor: HierarchyNode | null = parentNode;
      let isCycle = false;
      while (ancestor) {
        if (ancestor.entity.id === childNode.entity.id) {
          isCycle = true;
          break;
        }
        ancestor = ancestor.parent;
      }
      if (isCycle) continue;

      childNode.parent = parentNode;
      parentNode.children.push(childNode);
    }
  }

  // Root nodes are those with no parent
  const roots = Array.from(nodeMap.values()).filter(n => n.parent === null);

  return roots;
}

// ---------------------------------------------------------------------------
// Node lookup
// ---------------------------------------------------------------------------

/** Find a node by entity ID anywhere in a hierarchy tree.
 *
 * A `visited` set guards against infinite recursion if the graph
 * contains unexpected cycles.
 */
export function findNodeById(
  roots: HierarchyNode[],
  entityId: string,
  visited: Set<string> = new Set()
): HierarchyNode | null {
  for (const root of roots) {
    if (visited.has(root.entity.id)) continue;
    visited.add(root.entity.id);
    if (root.entity.id === entityId) return root;
    const found = findNodeById(root.children, entityId, visited);
    if (found) return found;
  }
  return null;
}

/** Collect ancestor IDs from a node up to the root (for auto-expanding tree paths). */
export function getAncestorIds(node: HierarchyNode): string[] {
  const ids: string[] = [];
  let current = node.parent;
  while (current) {
    ids.push(current.entity.id);
    current = current.parent;
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Power-flow extraction
// ---------------------------------------------------------------------------

/**
 * Extract only the power-flow subgraph (feeds relationships).
 *
 * Returns the subset of entities and relationships that participate in
 * `feeds` relationships, excluding Site entities. Useful for rendering
 * a simplified power distribution diagram.
 */
export function extractPowerFlow(
  entities: Entity[],
  relationships: Relationship[]
): { nodes: Entity[]; edges: Relationship[] } {
  const feedsEdges = relationships.filter(r => r.type === 'feeds');

  // Collect entity IDs participating in feeds relationships
  const participantIds = new Set<string>();
  for (const rel of feedsEdges) {
    participantIds.add(rel.source);
    participantIds.add(rel.target);
  }

  const nodes = entities.filter(
    e => participantIds.has(e.id) && cleanEntityType(e.type) !== 'Site'
  );

  // Only keep edges whose both endpoints are present in the returned node set
  // (Site entities may have been filtered out, leaving orphaned edges).
  const nodeIdSet = new Set(nodes.map(n => n.id));
  const filteredEdges = feedsEdges.filter(
    rel => nodeIdSet.has(rel.source) && nodeIdSet.has(rel.target)
  );

  return { nodes, edges: filteredEdges };
}
